import * as React from 'react';
import styled from 'styled-components';
import {useRoute} from 'react-router5';
import {deleteLesson, getLessons} from 'api/lesson/requests';
import {LessonObject} from 'api/lesson/types';
import Container from 'containers/Container';
import {ButtonVariant} from 'components/Button';
import Header from 'components/Header';
import validateResponse from 'helpers/validate-response';
import isError from 'helpers/is-error';
import {IconLoading} from 'icons';
import TeacherLessonPreview from 'components/TeacherLessonPreview';
import HeaderSearch from 'components/HeaderSearch';
import ButtonLink from 'components/ButtonLink';
import {FolderSelector} from '../components/FolderSelector';
import {Folder} from '../types';
import {Filter} from '../components/Filter';
import {CourseStatus} from '../api/course';
import Radio from '../components/Radio';
import {Helmet} from 'react-helmet';

export const Filters = styled.div`
  margin-top: 20px;
  justify-content: space-between;
  display: flex;
`;

export const StyledSelector = styled.div`
  margin-bottom: 14px;
`;

const LessonsContainer = styled.div`
  gap: 10px;
  display: grid;
  margin-bottom: 120px;
`;

/**
 * The courses page
 * @constructor
 */
export default function Lessons(): React.ReactElement {
  const {route} = useRoute();

  const [
    folder, setFolder,
  ] = React.useState<Folder | undefined>(undefined);

  const [
    isLoaded,
    setLoaded,
  ] = React.useState<boolean>(false);

  const [
    lessons,
    setLessons,
  ] = React.useState<LessonObject[]>([]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      fetchLessons();
    }, 600);
    return () => clearTimeout(timeout);
  }, [
    route.params.search,
    folder,
  ]);

  /**
   * Fetch lessons.
   */
  function fetchLessons(): void {
    setLoaded(false);

    getLessons({
      my: '1',
      title: route.params.search,
      folder,
    }).then((response) => {
      validateResponse(response);

      if (response && !isError(response)) {
        setLessons(response);
        setLoaded(true);
      }
    });
  }

  /**
   * This function renders the page header.
   * @return {JSX.Element}
   */
  function renderHeader(): JSX.Element {
    return (
      <Header>
        <HeaderSearch
          inputSearch={{
            placeholder: 'Поиск по названию урока',
          }}
          renderAfter={(
            <ButtonLink href="/lessons/create" variant={ButtonVariant.Primary}>
              Создать урок
            </ButtonLink>
          )}
        />
        <Filters>
          <Filter controls>
            <Filter.Group name="status" label="Публикация">
              {(group) => (
                [
                  (
                    <Filter.Field
                      type="radio"
                      label="Опубликовано"
                      value={CourseStatus.Public}>
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Опубликовано"
                        />
                      )}
                    </Filter.Field>
                  ),
                  (
                    <Filter.Field
                      type="radio"
                      label="Не опубликовано"
                      value={CourseStatus.Draft}>
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Не опубликовано"
                        />
                      )}
                    </Filter.Field>
                  ),
                ]
              )}
            </Filter.Group>
            <Filter.Group name="createdAt" label="Дата создания">
              {(group) => (
                [
                  (
                    <Filter.Field
                      type="radio"
                      label="Сегодня"
                      value="today">
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Сегодня"
                        />
                      )}
                    </Filter.Field>
                  ),
                  (
                    <Filter.Field
                      type="radio"
                      label="Вчера"
                      value="tomorrow">
                      {(field) => (
                        <Radio
                          name={group.name}
                          value={field.value}
                          checked={field.checked}
                          onChange={field.onChange}
                          label="Вчера"
                        />
                      )}
                    </Filter.Field>
                  ),
                ]
              )}
            </Filter.Group>
          </Filter>
        </Filters>
      </Header>
    );
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    return (
      <Container>
        <StyledSelector>
          <FolderSelector
            type="lessons"
            onChange={(folders) => (
              setFolder(folders[0])
            )}
          />
        </StyledSelector>
        {isLoaded ? (
          renderLessons()
        ) : <IconLoading />}
      </Container>
    );
  }

  /**
   * Render courses.
   * @return {JSX.Element}
   */
  function renderLessons(): JSX.Element {
    /**
     * Render the course.
     * @param {LessonObject} lesson
     * @param {number} index
     * @return {JSX.Element}
     */
    function renderCourse(
        lesson: LessonObject,
        index: number,
    ): JSX.Element {
      /**
       * This delete event handler.
       * @param {LessonObject<id>} id
       */
      function handleDelete(id: LessonObject['id']): void {
        deleteLesson(id).then((response) => {
          validateResponse(response);

          if (response && !isError(response)) {
            fetchLessons();
          }
        });
      }

      return (
        <TeacherLessonPreview
          key={index}
          {...lesson}
          onDelete={handleDelete}
        />
      );
    }

    /**
     * Render search not found.
     * @return {JSX.Element | undefined}
     */
    function renderSearchNotFound(): JSX.Element | undefined {
      return (route.params.search?.length > 0 && lessons.length === 0) ? (
        <React.Fragment>
          <h2>По запросу {`'`}{route.params.search}{`'`} - ничего не найдено</h2>
        </React.Fragment>
      ) : undefined;
    }

    return renderSearchNotFound() || (
      <LessonsContainer>
        {lessons.map((course, index) => (
          renderCourse(course, index)
        ))}
      </LessonsContainer>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>GrowPlace / Все уроки</title>
      </Helmet>
      {renderHeader()}
      {renderContent()}
    </React.Fragment>
  );
}
